import { FC, useEffect } from "react"

import { Skeleton, Stack, Typography } from "@mui/material"
import { useInfiniteQueryBankerGroupMembers } from "api/reactQuery/queries/groups"
import Dialog from "components/common/dialog/dialog"
import { useTranslation } from "react-i18next"
import { IGroup, IGroupMember } from "ts/interfaces/Group"

interface IProps {
  open: boolean
  classId: number
  group: IGroup
  onClose: () => void
}

const GroupMembersDialog: FC<IProps> = ({ open, classId, group, onClose }) => {
  const { t } = useTranslation()

  const needsFullMembers = group.members_count > group.members_preview.length

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQueryBankerGroupMembers({
      classId,
      groupId: group.id,
      options: { enabled: open && needsFullMembers },
    })

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const isLoadingFullMembers =
    needsFullMembers && (isLoading || hasNextPage || isFetchingNextPage)

  const members: IGroupMember[] = needsFullMembers
    ? (data?.pages.flatMap((page) => page.data.objects) ?? [])
    : group.members_preview

  return (
    <Dialog
      open={open}
      onClose={onClose}
      titleText={group.name}
      tabletMaxWidth="440px"
      customActionButtonsSlot={null}
    >
      <Stack gap="4px">
        {isLoadingFullMembers ? (
          Array.from({ length: group.members_count }).map((_, index) => (
            <Skeleton key={index} variant="text" width="60%" />
          ))
        ) : group.members_count === 0 ? (
          <Typography variant="body2" color="mockup.neutral40">
            {t("group.noMembers")}
          </Typography>
        ) : (
          members.map((member) => (
            <Typography
              key={member.id}
              variant="body2"
              color="mockup.neutral10"
            >
              {member.first_name} {member.last_name}
            </Typography>
          ))
        )}
      </Stack>
    </Dialog>
  )
}

export default GroupMembersDialog
